import { useRetailer } from "@faire/retailer-visitor-shared/hooks/retailer/useRetailer";
import { getWindow } from "@faire/web--source/common/globals/getWindow";
import { StrictLocalizeFunction } from "@faire/web--source/common/localization";
import {
  useMessengerStoreBrandContext,
  useMessengerStoreGetConversationOrUndefined,
} from "@faire/web--source/messenger/store/useMessengerStore";
import { IBrand } from "@faire/web-api--source/indigofair/data/IBrand";
import { IProductPageResponse } from "@faire/web-api--source/indigofair/data/IProductPageResponse";
import confirmIdentity from "@faire/web-api--source/routes/www/confirm-identity";
import messages from "@faire/web-api--source/routes/www/messages/otherPartyToken";
import { useMutation } from "@tanstack/react-query";

import { useIsLoggedInRetailerOrBrandPreview } from "./isLoggedInRetailer";

export const useContactBrand = (brand?: IBrand) => {
  const { retailer } = useRetailer();
  const canMessageBrand = useGetCanMessageBrand()(brand);

  if (retailer?.identity_confirmed && canMessageBrand) {
    return () => getWindow()?.open(messages(brand?.token ?? ""), "_blank");
  }

  return undefined;
};

export const useGetCanMessageBrand = () => {
  const { hasConversationWithBrand } =
    useMessengerStoreGetConversationOrUndefined();
  const isLoggedInRetailerOrBrandPreview =
    useIsLoggedInRetailerOrBrandPreview();

  return (
    brand:
      | Pick<IProductPageResponse.IPdpBrand, "token" | "hide_chat" | "active">
      | undefined
  ): boolean => {
    return !!(
      isLoggedInRetailerOrBrandPreview &&
      brand?.token &&
      brand.active &&
      !brand.hide_chat &&
      hasConversationWithBrand(brand.token)
    );
  };
};

export const useShouldShowMessageBrandCTA = (
  brand: Pick<IProductPageResponse.IPdpBrand, "token" | "hide_chat" | "active">,
  identityConfirmed: boolean
): boolean => {
  const getCanMessageBrand = useGetCanMessageBrand();
  return getCanMessageBrand(brand) || !identityConfirmed;
};

export const useShouldShowVerificationTooltip = (
  brand: Pick<IProductPageResponse.IPdpBrand, "token" | "hide_chat" | "active">,
  identityConfirmed: boolean
): boolean => {
  const getCanMessageBrand = useGetCanMessageBrand();
  return !getCanMessageBrand(brand) && !identityConfirmed;
};

export const getUnverifiedRetailerTooltipLabels = (
  strictLocalize: StrictLocalizeFunction
) => {
  return {
    title: strictLocalize({
      defaultMessage: "Your account is in review",
      description: {
        text: "Our team is verifying the identity of this user account",
      },
    }),
    message: strictLocalize({
      defaultMessage:
        "Sorry, only verified accounts can message this brand directly. All retailers go through our review process—once your account is confirmed, you'll be able to message this brand.",
    }),
  };
};

export const getIdvTransparencyMessage = (
  strictLocalize: StrictLocalizeFunction,
  pendingIdv: boolean,
  isRetailerSubUser: boolean
) => {
  if (pendingIdv) {
    if (isRetailerSubUser) {
      return {
        messageLabel: strictLocalize({
          defaultMessage:
            "To message brands, please have your account owner verify your team account.",
          description: {
            text: "Message shown to sub-users who have not completed IDV",
            img: "https://cdn.faire.com/fastly/a4a1a186b80f4f6218c0db7bc01b2612ccb24eab970ac04343d1621e1374a5c2.png",
          },
        }),
        action: undefined,
      };
    }
    return {
      messageLabel: strictLocalize({
        defaultMessage: "To message brands, please verify your account.",
      }),
      action: {
        label: strictLocalize({
          defaultMessage: "Verify Account",
          description: {
            text: "Link that takes retailer to complete IDV form",
          },
        }),
        link: confirmIdentity(),
      },
    };
  } else {
    return {
      messageLabel: strictLocalize({
        defaultMessage:
          "You’ll be able to message brands as soon as your account is verified. We’re reviewing your info now.",
      }),
      action: undefined,
    };
  }
};

export const useSetMessengerBrandContext = () => {
  const { setCurrentBrandContext } = useMessengerStoreBrandContext();
  return useMutation({
    mutationKey: ["current-brand-context"],
    mutationFn: (brand: IBrand | undefined) => setCurrentBrandContext(brand),
  });
};
